export type RaffleScheduleChipKind = 'open' | 'full' | 'closed' | 'soon' | 'drawing' | 'voided' | 'unavailable';

/** What the Sorteios hero should render for the current event. */
export type RaffleHeroPhase = 'open' | 'full' | 'drawing' | 'none';

const TERMINAL = ['settled', 'paid'];
const VOIDED = ['voided', 'cancelled', 'canceled'];
// Entries are closed and the event is still on its way to a result.
const HELD = ['locked', 'full', 'settling', 'drawing'];

/**
 * A held draw is only "drawing" once its scheduled moment has arrived. The
 * backend locks an event the instant its last seat is taken, which can be a
 * full hour early — labelling that whole hour "Drawing" tells players the
 * result is imminent when it is not, and hides the fact that they can still
 * see who got in. Pass the draw time to get that distinction; without it the
 * conservative "drawing" answer stands.
 */
export function raffleStatusChipKind(status: string, scheduledAt?: number, nowSec?: number): RaffleScheduleChipKind {
  const value = (status || '').toLowerCase();
  if (value === 'registering') return 'open';
  if (TERMINAL.includes(value)) return 'closed';
  if (VOIDED.includes(value)) return 'voided';
  if (HELD.includes(value)) {
    const at = Number(scheduledAt || 0);
    const now = Number(nowSec || 0);
    return at > 0 && now > 0 && now < at ? 'full' : 'drawing';
  }
  if (value === 'unavailable') return 'unavailable';
  return 'soon';
}

export function raffleScheduleChipKind(
  status: string,
  registrationStarts: number,
  nowSec: number,
  scheduledAt?: number,
): RaffleScheduleChipKind {
  if (registrationStarts > nowSec) return 'soon';
  return raffleStatusChipKind(status, scheduledAt, nowSec);
}

/**
 * The hero's state machine. A full or drawing event is still the page's main
 * story — it has a pool, a countdown and, for most players, their own numbers
 * in it — so it must never collapse into the "no draw open" empty line.
 */
export function raffleHeroPhase({
  status,
  totalEntries,
  maxTotalEntries,
  scheduledAt,
  nowSec,
}: {
  status: string;
  totalEntries: number;
  maxTotalEntries: number;
  scheduledAt: number;
  nowSec: number;
}): RaffleHeroPhase {
  const kind = raffleStatusChipKind(status, scheduledAt, nowSec);
  if (kind === 'drawing') return 'drawing';
  if (kind === 'full') return 'full';
  if (kind !== 'open') return 'none';
  // Seats and status are written in the same transaction, but a stale read of
  // either must degrade to the honest "full" screen, never to "nothing here".
  const seats = Math.max(0, Number(maxTotalEntries) || 0);
  const taken = Math.max(0, Number(totalEntries) || 0);
  if (seats > 0 && taken >= seats) {
    return Number(scheduledAt) > Number(nowSec) ? 'full' : 'drawing';
  }
  return 'open';
}
